import React, { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { CircularProgress, styled, useTheme } from '@mui/material'
import { useFeatureFlag } from '../../../utils/hooks/useFeatureFlag'
import { setCMSHealthInfo } from '../../../store/serviceHealth/serviceHealthSlice'
import {
  ArticleMetaData,
  getSpotlightArticle,
} from '../../../utils/helpers/Article.service'
import SpotlightArticle from './SpotlightArticle'
import BrandEvolutionSpotlightArticle from './BrandEvolutionSpotlightArticle'
import {
  setHasPopBgColor,
  setHasPopBgImg,
  setPopBgColor,
  setPopBgImg,
  setBannerSubHeaderCopy,
} from '../../../store/spotlightArticle/spotlightArticleSlice'
import { userAppsStyles } from '../../UserApp/UserApps.styles'

const StyledLoadingContainer = styled('div')(({ theme }) => ({
  backgroundColor: theme.palette.common.white,
  height: '37.5rem',
  paddingTop: '15.75rem',
}))

const SpotlightArticleWrapper = () => {
  const [articleMetadata, setArticleMetaData] = useState<ArticleMetaData>()
  const [isLoaded, setIsLoaded] = useState<boolean>(false)
  const dispatch = useDispatch()
  const isBrandEvolutionEnabled = useFeatureFlag(
    'SpotlightArticleBrandEvolution',
  )
  const isAdminSettingsFlagEnabled = useFeatureFlag('AdminSettings')

  const theme = useTheme()
  const classes = userAppsStyles(theme)

  const init = async () => {
    try {
      const article = await getSpotlightArticle(isAdminSettingsFlagEnabled)

      if (
        article &&
        article.bannerPoPBackgroundColor &&
        article.bannerPoPBackgroundColor.length > 0
      ) {
        dispatch(setPopBgColor(article.bannerPoPBackgroundColor))
        dispatch(setHasPopBgColor(true))
      }

      if (
        article &&
        article.bannerPoPBackgroundPathDesktop &&
        article.bannerPoPBackgroundPathDesktop.length > 0
      ) {
        dispatch(setPopBgImg(article.bannerPoPBackgroundPathDesktop))
        dispatch(setHasPopBgImg(true))
      }

      if (article === undefined) {
        dispatch(
          setCMSHealthInfo({
            IsHealthy: false,
            ErrorMessage: 'No results found',
          }),
        )
      } else {
        dispatch(
          setCMSHealthInfo({
            IsHealthy: true,
            ErrorMessage: undefined,
          }),
        )
      }
      setArticleMetaData(article)
      setIsLoaded(true)
    } catch {
      dispatch(
        setCMSHealthInfo({
          IsHealthy: false,
          ErrorMessage: 'getSpotlightArticle call failed.',
        }),
      )
      setArticleMetaData(undefined)
      setIsLoaded(true)
    }

    if (isAdminSettingsFlagEnabled) {
      try {
        const key = 'SpotlightArticleSubHeaderCopy'
        const res = await fetch(
          `/api/v1/AdminSettings/GetByKey?key=${encodeURIComponent(key)}`,
          { credentials: 'include' },
        )
        if (res.ok) {
          const data = await res.json()
          const secondary =
            data?.secondaryText ??
            data?.data?.secondaryText ??
            data?.Data?.secondaryText ??
            ''
          dispatch(setBannerSubHeaderCopy(secondary || ''))
        } else {
          dispatch(setBannerSubHeaderCopy('')) // no admin text; fallback will use metadata
        }
      } catch {
        dispatch(setBannerSubHeaderCopy(''))
      }
    }
  }

  useEffect(() => {
    init()
  }, [])

  if (!isLoaded) {
    return (
      <StyledLoadingContainer>
        <CircularProgress
          sx={classes.circularProgress}
          size="6rem"
          data-testid="Spn_SpotlightArticle_Loading"
        />
      </StyledLoadingContainer>
    )
  }

  return isBrandEvolutionEnabled ? (
    <BrandEvolutionSpotlightArticle article={articleMetadata} />
  ) : (
    <SpotlightArticle article={articleMetadata} />
  )
}

export default SpotlightArticleWrapper
